import { BaseMutationOptions, useApolloClient } from "@apollo/client/react"
import { useTranslation } from "react-i18next"

import type { ApolloError } from "@apollo/client/core"

import { callMutation } from "shared/api/callMutation"
import {
  evictPricesByProductType,
  evictProjectPrice
} from "shared/api/price/evictPriceCache"
import { invalidatePricesByProductType } from "shared/api/price/modifyPriceCache"
import { generateUUID } from "shared/lib/generateUUID"
import { useProjectRequestMonitor } from "shared/lib/request-monitoring/useProjectRequestMonitor"
import { useEvent } from "shared/lib/useEvent"
import { useRequestToasts } from "shared/lib/useRequestToasts"
import { ApolloFetchPolicy } from "shared/types/ApolloFetchPolicy"
import { CREATE_SPECIAL_PRICE_GROUP } from "shared/types/ProjectMutations"
import { SpecialPriceGroup } from "shared/types/__generated__/graphql"

import { evictProjectHistory } from "entities/projects/graphql/project-history/evictProjectHistory"
import {
  useProjectChildrenLazyQuery,
  useProjectsLazyQuery
} from "entities/projects/graphql/queries.generated"
import {
  CreateSpecialPriceGroupMutation,
  CreateSpecialPriceGroupMutationVariables,
  useCreateSpecialPriceGroupMutation
} from "entities/projects/graphql/special-price-groups/mutations.generated"

import { evictSpecialPriceGroup } from "./evictSpecialPriceGroup"
import { insertSpecialPriceGroup } from "./insertSpecialPriceGroup"

export interface CreateSpecialPriceGroupArgs {
  projectID: string
  specialPriceGroup: Omit<SpecialPriceGroup, "id" | "name" | "parentPriceGroup">
}

export function useCreateSpecialPriceGroup() {
  const { t } = useTranslation()
  const { cache: apolloCache } = useApolloClient()
  const [fetchProject] = useProjectChildrenLazyQuery({
    fetchPolicy: ApolloFetchPolicy.NETWORK_ONLY
  })
  const [fetchProjects] = useProjectsLazyQuery()
  const startProjectRequest = useProjectRequestMonitor()
  const { raiseLoadingToast } = useRequestToasts({
    messages: {
      loading: t("Request.SpecialPriceGroup.CREATE_SPECIAL_PRICE_GROUP.Start"),
      success: t(
        "Request.SpecialPriceGroup.CREATE_SPECIAL_PRICE_GROUP.Success"
      ),
      failure: t("Request.SpecialPriceGroup.CREATE_SPECIAL_PRICE_GROUP.Failure")
    }
  })

  const handleError = useEvent(
    (
      error: ApolloError,
      options:
        | BaseMutationOptions<
            CreateSpecialPriceGroupMutation,
            Partial<CreateSpecialPriceGroupMutationVariables>
          >
        | undefined
    ) => {
      if (options?.variables?.input?.id) {
        evictSpecialPriceGroup(options.variables.input.id, apolloCache)
      }
    }
  )

  const [createSpecialPriceGroup] = useCreateSpecialPriceGroupMutation({
    onError: handleError,
    update: (cache, result, options) => {
      if (result.data?.specialPriceGroup && options.variables) {
        insertSpecialPriceGroup({
          group: result.data?.specialPriceGroup,
          projectID: options.variables?.projectID,
          cache
        })
      }
    }
  })

  return useEvent(
    async ({ projectID, specialPriceGroup }: CreateSpecialPriceGroupArgs) => {
      const id = generateUUID()

      // ensure special price groups are in the cache
      await fetchProject({ variables: { id: projectID } })

      const completedSpecialPriceGroup = {
        ...specialPriceGroup,
        name: specialPriceGroup.productType,
        id
      }

      const optimisticResponse: CreateSpecialPriceGroupMutation = {
        __typename: "Mutation",
        specialPriceGroup: {
          ...completedSpecialPriceGroup,
          parentPriceGroup: null
        }
      }

      invalidatePricesByProductType({
        projectID,
        cache: apolloCache,
        productType: completedSpecialPriceGroup.productType
      })

      const { raiseSuccessToast, raiseErrorToast } = raiseLoadingToast()
      const completeProjectRequest = startProjectRequest({
        projectID,
        requestType: CREATE_SPECIAL_PRICE_GROUP
      })

      return callMutation<
        CreateSpecialPriceGroupMutation,
        CreateSpecialPriceGroupMutationVariables,
        SpecialPriceGroup
      >(createSpecialPriceGroup, {
        variables: {
          projectID,
          input: completedSpecialPriceGroup
        },
        transform: data => data.specialPriceGroup,
        apolloOptions: {
          optimisticResponse,
          onCompleted: () => {
            evictPricesByProductType({
              projectID,
              productType: completedSpecialPriceGroup.productType,
              cache: apolloCache
            })
            completeProjectRequest()
            fetchProject({ variables: { id: projectID } })
            evictProjectHistory(projectID, apolloCache)
            raiseSuccessToast()
          },
          onError: () => {
            completeProjectRequest()
            fetchProject({ variables: { id: projectID } })
            evictProjectHistory(projectID, apolloCache)
            evictProjectPrice(projectID, apolloCache, true)
            fetchProjects()
            raiseErrorToast()
          }
        }
      })
    }
  )
}
